import { createFileRoute } from "@tanstack/react-router";
import {
  AgentAvatar,
  Avatar,
  Badge,
  Button,
  Card,
  IconButton,
  NavItem,
  SearchField,
  Field,
  Select,
  Menu,
  Sheet,
  SegmentedControl,
  Stat,
  StatusDot,
  Switch,
  Tabs
} from "@salesko/design-system";
import { Bell, Building2, CalendarDays, Plus, Sparkles, Star, Users } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/specimen")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Salesko Design System — Specimen" },
      { name: "robots", content: "noindex, nofollow" }
    ]
  }),
  component: SpecimenRoute
});

const BUTTON_VARIANTS = ["primary", "secondary", "ghost", "subtle", "danger"] as const;
const BUTTON_SIZES = ["sm", "md", "lg"] as const;
const ENTITY_TONES = ["person", "company", "event", "opportunity", "note"] as const;
const AVATAR_SIZES = ["xs", "sm", "md", "lg", "xl"] as const;
const AGENT_AVATAR_SIZES = ["xs", "sm", "md", "lg"] as const;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "40px" }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-title-lg)",
          fontWeight: 700,
          color: "var(--text-strong)",
          letterSpacing: "var(--tracking-tight)",
          margin: 0,
          borderBottom: "1px solid var(--border-subtle)",
          paddingBottom: "8px"
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

const ROW: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: "14px", alignItems: "center" };

function SpecimenRoute() {
  const [selectedControl, setSelectedControl] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [inlineSheetOpen, setInlineSheetOpen] = useState(false);
  const [menuResult, setMenuResult] = useState("");

  const [tab, setTab] = useState("people");
  const [nav, setNav] = useState("graph");
  const [search, setSearch] = useState("");
  const [switches, setSwitches] = useState<Record<string, boolean>>({
    brand: true,
    person: true,
    company: false,
    event: true,
    opportunity: false
  });

  return (
    <main
      style={{
        maxWidth: "960px",
        margin: "0 auto",
        padding: "48px 24px 96px",
        fontFamily: "var(--font-sans)",
        color: "var(--text-body)"
      }}
    >
      <header style={{ marginBottom: "40px" }}>
        <p
          style={{
            textTransform: "uppercase",
            letterSpacing: "var(--tracking-eyebrow)",
            fontSize: "var(--text-micro)",
            fontWeight: 700,
            color: "var(--text-muted)",
            margin: "0 0 8px"
          }}
        >
          Design system
        </p>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-display-md)",
            overflowWrap: "anywhere",
            fontWeight: 700,
            color: "var(--text-strong)",
            letterSpacing: "var(--tracking-tight)",
            margin: 0
          }}
        >
          @salesko/design-system specimen
        </h1>
      </header>

      <Section title="Button — variant × size">
        {BUTTON_SIZES.map((size) => (
          <div key={size} style={ROW}>
            {BUTTON_VARIANTS.map((variant) => (
              <Button key={variant} variant={variant} size={size}>
                {variant} {size}
              </Button>
            ))}
          </div>
        ))}
        <div style={ROW}>
          <Button iconLeft={<Sparkles size={16} />}>Generate graph frame</Button>
          <Button variant="secondary" iconRight={<Plus size={16} />}>
            Add
          </Button>
          <Button disabled>Disabled</Button>
          <Button fullWidth variant="subtle">
            Full width
          </Button>
        </div>
      </Section>

      <Section title="IconButton — variant × shape">
        <div style={ROW}>
          <IconButton icon={<Plus size={18} />} variant="solid" ariaLabel="Add" />
          <IconButton icon={<Bell size={18} />} variant="secondary" ariaLabel="Notifications" />
          <IconButton icon={<Star size={18} />} variant="ghost" ariaLabel="Star" />
          <IconButton icon={<Plus size={18} />} variant="solid" shape="circle" ariaLabel="Add" />
          <IconButton icon={<Star size={18} />} variant="secondary" size="sm" ariaLabel="Star" />
          <IconButton icon={<Bell size={18} />} variant="secondary" size="lg" ariaLabel="Notifications" />
          <IconButton icon={<Plus size={18} />} disabled ariaLabel="Add" />
        </div>
      </Section>

      <Section title="Avatar — size, tone, glow">
        <div style={ROW}>
          {AVATAR_SIZES.map((size) => (
            <Avatar key={size} size={size} tone="person" initials="AC" />
          ))}
        </div>
        <div style={ROW}>
          <Avatar tone="person" initials="AC" glow />
          <Avatar tone="company" icon={<Building2 size={18} color="#fff" />} glow />
          <Avatar tone="event" icon={<CalendarDays size={18} color="#fff" />} glow />
          <Avatar tone="opportunity" icon={<Star size={18} color="#fff" />} glow />
          <Avatar tone="note" icon={<Users size={18} color="#fff" />} />
        </div>
      </Section>

      <Section title="AgentAvatar — variant × size (agents are squared, people are round)">
        <div style={ROW}>
          {AGENT_AVATAR_SIZES.map((size) => (
            <AgentAvatar key={`generic-${size}`} size={size} variant="generic" />
          ))}
        </div>
        <div style={ROW}>
          {AGENT_AVATAR_SIZES.map((size) => (
            <AgentAvatar key={`private-${size}`} size={size} variant="private" />
          ))}
        </div>
        <div style={ROW}>
          <AgentAvatar srLabel="Generic Agent" variant="generic" />
          <Avatar tone="person" initials="AC" />
          <AgentAvatar srLabel="Private Agent" variant="private" />
        </div>
      </Section>

      <Section title="StatusDot — online, offline, revoked">
        <div style={ROW}>
          <StatusDot status="online" label="Online" />
          <StatusDot status="offline" label="Offline" />
          <StatusDot status="revoked" label="Revoked" />
        </div>
        <div style={ROW}>
          <StatusDot size="sm" status="online" label="Online" />
          <StatusDot size="sm" status="offline" label="Offline" />
          <StatusDot size="sm" status="revoked" label="Revoked" />
        </div>
      </Section>

      <Section title="Badge — tone × variant">
        <div style={ROW}>
          {ENTITY_TONES.map((tone) => (
            <Badge key={tone} tone={tone}>
              {tone}
            </Badge>
          ))}
          <Badge tone="neutral">neutral</Badge>
        </div>
        <div style={ROW}>
          {ENTITY_TONES.map((tone) => (
            <Badge key={tone} tone={tone} variant="dot">
              {tone}
            </Badge>
          ))}
        </div>
      </Section>

      <Section title="Stat — tone">
        <div style={ROW}>
          <Stat value="1,284" label="People" tone="person" />
          <Stat value="328" label="Companies" tone="company" />
          <Stat value="642" label="Events" tone="event" />
          <Stat value="47" label="Opportunities" tone="opportunity" />
          <Stat value="12" label="Frames" tone="brand" />
        </div>
      </Section>

      <Section title="SearchField — size, glass">
        <SearchField value={search} onChange={setSearch} />
        <SearchField value={search} onChange={setSearch} size="md" kbd={null} />
        <div style={{ padding: "16px", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)" }}>
          <SearchField value={search} onChange={setSearch} glass placeholder="Search over the graph…" />
        </div>
      </Section>

      <Section title="Switch — tone, on/off, size">
        <div style={ROW}>
          {(["brand", "person", "company", "event", "opportunity"] as const).map((tone) => (
            <Switch
              key={tone}
              tone={tone}
              checked={switches[tone] ?? false}
              onChange={(next) => setSwitches((s) => ({ ...s, [tone]: next }))}
              ariaLabel={tone}
            />
          ))}
          <Switch size="sm" checked ariaLabel="small on" />
          <Switch disabled checked ariaLabel="disabled" />
        </div>
      </Section>

      <Section title="Card — tone, padding, interactive">
        <div style={ROW}>
          <Card style={{ width: "220px" }}>Default card</Card>
          <Card tone="sunken" style={{ width: "220px" }}>
            Sunken card
          </Card>
          <Card tone="accent" style={{ width: "220px" }}>
            Accent card
          </Card>
          <Card interactive style={{ width: "220px" }}>
            Interactive (hover lift)
          </Card>
        </div>
      </Section>

      <Section title="Tabs — counts, active">
        <Tabs
          style={{ flexWrap: "wrap" }}
          value={tab}
          onChange={setTab}
          items={[
            { id: "people", label: "People", count: 1284, icon: <Users size={15} /> },
            { id: "companies", label: "Companies", count: 328, icon: <Building2 size={15} /> },
            { id: "events", label: "Events", count: 642, icon: <CalendarDays size={15} /> }
          ]}
        />
        <p style={{ color: "var(--text-muted)", fontSize: "var(--text-body-sm)" }}>Active tab: {tab}</p>
      </Section>

      <Section title="shadcn — forms, menus and sheet">
        <Field label="Migration field" hint="Controlled input" value={fieldValue} onChange={setFieldValue} maxLength={20} />
        <Select label="Migration selection" value={selectedControl} onChange={setSelectedControl}
          options={[{label: "Choose an option", value: ""}, {label: "Alpha", value: "alpha"}, {label: "Beta", value: "beta"}, {label: "Unavailable", value: "disabled", disabled: true}]} />
        <p>Selected option: {selectedControl || "none"}</p>
        <SegmentedControl options={["One", "Two"]} value={menuResult} onChange={setMenuResult} />
        <Menu trigger={<Button>Open actions</Button>} items={[
          {label: "First action", onClick: () => setMenuResult("First action")},
          {label: "Unavailable action", disabled: true},
          {label: "Second action", onClick: () => setMenuResult("Second action")}
        ]} />
        <p>Action: {menuResult}</p>
        <Button onClick={() => setSheetOpen(true)}>Open sheet</Button>
        <Sheet open={sheetOpen} title="Migration sheet" onClose={() => setSheetOpen(false)}>
          <Field label="Sheet input" />
        </Sheet>
        <div className="relative min-h-60 overflow-hidden rounded-lg border border-solid border-border p-4">
          <Button onClick={() => setInlineSheetOpen(true)}>Open inline sheet</Button>
          <Sheet open={inlineSheetOpen} side="top" container="inline" title="Inline sheet" onClose={() => setInlineSheetOpen(false)}>
            <Field label="Inline sheet input" />
          </Sheet>
        </div>
      </Section>
      <Section title="NavItem — active, count">
        <div style={{ width: "268px", display: "flex", flexDirection: "column", gap: "2px" }}>
          <NavItem icon={<Users size={18} />} label="Overview" active={nav === "overview"} onClick={() => setNav("overview")} />
          <NavItem icon={<Sparkles size={18} />} label="Graph" active={nav === "graph"} onClick={() => setNav("graph")} />
          <NavItem icon={<Building2 size={18} />} label="Companies" count={328} active={nav === "companies"} onClick={() => setNav("companies")} />
          <NavItem icon={<CalendarDays size={18} />} label="Events" count={642} active={nav === "events"} onClick={() => setNav("events")} />
        </div>
      </Section>
    </main>
  );
}
